import { memo, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { CheckCircle, Edit2, Trash2, Sparkles, Clock, XCircle, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Label } from '@caruso/ui';
import { toast } from 'sonner';

interface VenueConfirmBookingData {
  // Confirmation Message
  confirmationMessage?: string;
  useHumanizedConfirmation?: boolean;

  // After Confirmation
  createPendingReservation?: boolean; // Cria reserva com status pendente
  sendPaymentLink?: boolean; // Envia link de pagamento (se houver)

  // Timeout
  waitTimeoutMinutes?: number; // Tempo máximo de espera pela resposta
}

export const VenueConfirmBookingNode = memo(({ data, id }: NodeProps<VenueConfirmBookingData>) => {
  const { deleteElements, setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);

  // State
  const [confirmationMessage, setConfirmationMessage] = useState(
    data.confirmationMessage ||
    'Perfeito! Sua reserva está quase pronta. Confirme os dados:\n\n📍 Ambiente: {{nome_ambiente}}\n📅 Data: {{data_reserva}}\n⏰ Horário: {{horario_reserva}}\n💰 Valor: {{valor_reserva}}\n\nDeseja confirmar?'
  );
  const [useHumanizedConfirmation, setUseHumanizedConfirmation] = useState(data.useHumanizedConfirmation ?? true);
  const [createPendingReservation, setCreatePendingReservation] = useState(data.createPendingReservation ?? true);
  const [sendPaymentLink, setSendPaymentLink] = useState(data.sendPaymentLink ?? false);
  const [waitTimeoutMinutes, setWaitTimeoutMinutes] = useState(data.waitTimeoutMinutes ?? 60);

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSave = () => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              confirmationMessage,
              useHumanizedConfirmation,
              createPendingReservation,
              sendPaymentLink,
              waitTimeoutMinutes,
            },
          };
        }
        return node;
      })
    );

    setIsEditing(false);
    toast.success('Confirmação de reserva configurada');
  };

  const handleCancel = () => {
    setConfirmationMessage(
      data.confirmationMessage ||
      'Perfeito! Sua reserva está quase pronta. Confirme os dados:\n\n📍 Ambiente: {{nome_ambiente}}\n📅 Data: {{data_reserva}}\n⏰ Horário: {{horario_reserva}}\n💰 Valor: {{valor_reserva}}\n\nDeseja confirmar?'
    );
    setUseHumanizedConfirmation(data.useHumanizedConfirmation ?? true);
    setCreatePendingReservation(data.createPendingReservation ?? true);
    setSendPaymentLink(data.sendPaymentLink ?? false);
    setWaitTimeoutMinutes(data.waitTimeoutMinutes ?? 60);
    setIsEditing(false);
  };

  return (
    <>
      <div className="bg-gradient-to-br from-green-900/90 to-green-950 border-2 border-green-400/60 rounded-lg p-4 min-w-[280px] shadow-lg shadow-green-500/20 relative">
        <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-green-400" />

        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-green-400 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-950" />
          </div>
          <div className="flex-1">
            <span className="font-bold text-white">Confirmar Reserva</span>
            <div className="flex items-center gap-1 text-xs text-green-300">
              <Sparkles className="w-3 h-3" />
              Finalização
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="text-green-400 hover:text-white transition-colors p-1"
              title="Editar confirmação"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="text-red-400 hover:text-red-300 transition-colors p-1"
              title="Deletar nó"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="space-y-2 text-xs">
          <div className="p-2 bg-green-800/30 rounded text-green-300/80">
            <div className="flex items-center gap-1 mb-1">
              <CheckCircle className="w-3 h-3" />
              <span>Cria reserva: {createPendingReservation ? 'Sim (pendente)' : 'Não'}</span>
            </div>
            {sendPaymentLink && (
              <div className="flex items-center gap-1">
                <span>💳 Envia link de pagamento</span>
              </div>
            )}
            {useHumanizedConfirmation && (
              <div className="flex items-center gap-1 text-purple-300">
                <Sparkles className="w-3 h-3" />
                <span>Mensagens humanizadas</span>
              </div>
            )}
            <div className="flex items-center gap-1 text-yellow-300 mt-1">
              <Clock className="w-3 h-3" />
              <span>Timeout: {waitTimeoutMinutes}min</span>
            </div>
          </div>
        </div>

        {/* Saídas com handles posicionados corretamente */}
        <Handle
          type="source"
          position={Position.Right}
          id="confirmed"
          className="!w-3 !h-3 !bg-green-400"
          style={{ top: '30%' }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="declined"
          className="!w-3 !h-3 !bg-red-400"
          style={{ top: '55%' }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="timeout"
          className="!w-3 !h-3 !bg-yellow-400"
          style={{ top: '80%' }}
        />

        {/* Labels das saídas */}
        <div className="absolute -right-1 top-0 h-full flex flex-col justify-around py-4 pr-6 pointer-events-none">
          <span className="text-[9px] text-green-400 font-medium">Sim</span>
          <span className="text-[9px] text-red-400 font-medium">Não</span>
          <span className="text-[9px] text-yellow-400 font-medium">Timeout</span>
        </div>
      </div>

      {/* Dialog de Configuração */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-neutral-900 border-green-500/30 text-white min-w-[800px] max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Configurar Confirmação de Reserva
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/70">
              Configure como a reserva será confirmada e criada
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Mensagem de Confirmação */}
            <div className="p-4 bg-green-500/5 rounded-lg border border-green-500/20">
              <h3 className="text-green-400 font-semibold mb-3">Mensagem de Confirmação</h3>

              <div>
                <Label className="text-brand-ivory/80 text-sm">Template da mensagem</Label>
                <textarea
                  value={confirmationMessage}
                  onChange={(e) => setConfirmationMessage(e.target.value)}
                  className="w-full mt-1 p-3 bg-neutral-800/50 border border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg resize-none text-sm font-mono"
                  rows={8}
                />
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-brand-ivory/50 font-medium">Variáveis disponíveis:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-brand-ivory/50">
                    <span>{'{{nome_ambiente}}'} - Nome do ambiente</span>
                    <span>{'{{data_reserva}}'} - Data formatada</span>
                    <span>{'{{horario_reserva}}'} - Horário início/fim</span>
                    <span>{'{{valor_reserva}}'} - Valor formatado</span>
                    <span>{'{{nome_cliente}}'} - Nome do cliente</span>
                    <span>{'{{periodo}}'} - Tarde/Noite</span>
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer mt-4">
                <input
                  type="checkbox"
                  checked={useHumanizedConfirmation}
                  onChange={(e) => setUseHumanizedConfirmation(e.target.checked)}
                  className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-green-500"
                />
                <span className="text-sm text-brand-ivory">Humanizar mensagem com IA</span>
              </label>
            </div>

            {/* Ações Pós-Confirmação */}
            <div className="p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
              <h3 className="text-blue-400 font-semibold mb-3">Ações Após Confirmação</h3>

              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createPendingReservation}
                    onChange={(e) => setCreatePendingReservation(e.target.checked)}
                    className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-blue-500"
                  />
                  <span className="text-sm text-brand-ivory">Criar reserva com status "Pendente"</span>
                </label>
                <p className="text-xs text-brand-ivory/50 ml-6">
                  A reserva será criada no sistema e aparecerá na lista de reservas pendentes para aprovação.
                </p>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendPaymentLink}
                    onChange={(e) => setSendPaymentLink(e.target.checked)}
                    className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-blue-500"
                  />
                  <span className="text-sm text-brand-ivory">Enviar link de pagamento</span>
                </label>
                <p className="text-xs text-brand-ivory/50 ml-6">
                  Se o ambiente tiver pagamento configurado, envia o link para o cliente.
                </p>
              </div>
            </div>

            {/* Timeout */}
            <div className="p-4 bg-yellow-500/5 rounded-lg border border-yellow-500/20">
              <h3 className="text-yellow-400 font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Tempo de Espera (Timeout)
              </h3>
              <div className="space-y-2">
                <Label className="text-brand-ivory/80 text-sm">Tempo máximo para resposta (minutos)</Label>
                <input
                  type="number"
                  value={waitTimeoutMinutes}
                  onChange={(e) => setWaitTimeoutMinutes(parseInt(e.target.value) || 60)}
                  min="1"
                  max="1440"
                  className="w-full p-2 bg-neutral-800/50 border border-neutral-700 text-white placeholder:text-neutral-500 rounded focus:outline-none focus:ring-1 focus:ring-yellow-500/50"
                />
                <p className="text-xs text-brand-ivory/50">
                  Se o cliente não responder dentro deste tempo, o fluxo seguirá pela saída "Timeout".
                </p>
              </div>
            </div>

            {/* Saídas do Nó */}
            <div className="p-4 bg-purple-500/5 rounded-lg border border-purple-500/20">
              <h3 className="text-purple-400 font-semibold mb-3">Saídas do Nó:</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-medium">Sim</span>
                  <span className="text-brand-ivory/60">- Cliente confirmou a reserva</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 font-medium">Não</span>
                  <span className="text-brand-ivory/60">- Cliente recusou a reserva</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-400" />
                  <span className="text-yellow-400 font-medium">Timeout</span>
                  <span className="text-brand-ivory/60">- Cliente não respondeu no tempo</span>
                </div>
              </div>
            </div>

            {/* Funcionamento */}
            <div className="p-4 bg-green-500/5 rounded-lg border border-green-500/20">
              <h3 className="text-green-400 font-semibold mb-2">Como funciona:</h3>
              <ol className="text-xs text-brand-ivory/60 space-y-1 list-decimal list-inside">
                <li>Recebe os dados do "Wizard de Reserva" (ambiente, data, horário)</li>
                <li>Exibe resumo e pede confirmação ao cliente</li>
                <li>Interpreta resposta (sim/não/dúvida) usando IA</li>
                <li>Se confirmar: cria reserva → saída "Sim"</li>
                <li>Se negar: → saída "Não"</li>
                <li>Se não responder no tempo: → saída "Timeout"</li>
              </ol>
            </div>

            {/* Botões */}
            <div className="flex gap-2 justify-end pt-2">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="border-neutral-700 text-white hover:bg-neutral-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                className="bg-green-500 hover:bg-green-600 text-white font-semibold"
              >
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

VenueConfirmBookingNode.displayName = 'VenueConfirmBookingNode';
